// GTW-56: pure change-history logic used by InsertCrudHistory (Functions.jsx).
// No React, no I/O, so it is unit tested (__tests__/crudHistory.test.js).
//
// CrudHistory records are readable by the tenant's own users, kept for a year
// and pass through the AppSync authorizer logs, and Changes.oldValue/newValue
// are String! in AppSync. So a change value is never a serialised structure
// and never a secret:
//   - sensitive field (name matches SENSITIVE_FIELD_PATTERN or is one of the
//     backend SecretKeys): "********" when set ("******** (changed)" on the new
//     side), "" when empty. A value the caller already masked (a string that
//     starts with "********") passes through untouched.
//   - string: as is; number / boolean: String(v); null / undefined: "".
//   - object / array: the literal "(changed)". A caller that wants structured
//     content in the history passes an already-serialised, non-secret string.
// A caller can declare fields it has already decided are NOT secret (e.g.
// identifiers such as Username / ClientID) with options.clearFields; those are
// recorded as plain values.

export const HISTORY_MASK = "********";
export const HISTORY_MASK_CHANGED = HISTORY_MASK + " (changed)";
export const CHANGED_MARKER = "(changed)";

// Backend ssmfunctions.SecretKeys: every integration field the backend diverts
// to SSM.
export const SECRET_KEYS = [
  "Password",
  "ClientSecret",
  "Headers",
  "Username",
  "TenantID",
  "ClientID",
  "AuthUrl",
  "RefreshToken",
  "Scope",
  "AuthCode",
  "AccessTokenValidUntil",
  "AccessToken",
  "Resource",
  "Code",
  "RedirectUri",
];

export const SENSITIVE_FIELD_PATTERN = /pass|key|secret|token|authoriz|credential/i;
// NEW-A: a header map (e.g. FieldActions' requestHeaders, a JSON string with
// free keys such as Authorization / x-api-key) is sensitive as a whole.
export const HEADERS_FIELD_PATTERN = /headers?$/i;

// Fields never recorded (bookkeeping), as in the original GetChanges. Keys
// that only exist on the new side skip only tableData, also as before.
const EXCLUDED_FIELDS = [
  "tableData",
  "id",
  "createdAt",
  "updatedAt",
  "customerID",
  "owner",
];

// A key that marks its value as secret by name (the pattern, or a header map).
const isSensitiveName = (name) =>
  SENSITIVE_FIELD_PATTERN.test(String(name)) ||
  HEADERS_FIELD_PATTERN.test(String(name));

export const isSensitiveField = (field) =>
  SECRET_KEYS.includes(field) || isSensitiveName(field);

const isEmpty = (value) =>
  value === null || value === undefined || value === "";

// NEW-B: only exactly the mask or its "(changed)" variant counts as already
// masked by the caller - "********real-token" is not.
const isPreMasked = (value) =>
  value === HISTORY_MASK || value === HISTORY_MASK_CHANGED;

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// NEW-A/NEW-C: URLs can carry credentials in their userinfo
// (https://user:pass@host) and in query / fragment values (?code=..., ?key=...,
// #access_token=...). Keep scheme://host/path and the query keys; mask the rest.
const URL_PATTERN = /\b[a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+/gi;

const scrubUrl = (url) => {
  const schemeEnd = url.indexOf("://") + 3;
  let rest = url.slice(schemeEnd);
  let fragment = "";
  const hashAt = rest.indexOf("#");
  if (hashAt >= 0) {
    fragment = rest.slice(hashAt + 1) ? "#***" : "#";
    rest = rest.slice(0, hashAt);
  }
  let query = null;
  const queryAt = rest.indexOf("?");
  if (queryAt >= 0) {
    query = rest.slice(queryAt + 1);
    rest = rest.slice(0, queryAt);
  }
  const slashAt = rest.indexOf("/");
  let authority = slashAt >= 0 ? rest.slice(0, slashAt) : rest;
  const path = slashAt >= 0 ? rest.slice(slashAt) : "";
  const atAt = authority.lastIndexOf("@");
  if (atAt >= 0) authority = "***@" + authority.slice(atAt + 1);
  const maskedQuery =
    query === null
      ? ""
      : "?" +
        query
          .split("&")
          .map((pair) => {
            if (pair === "") return pair;
            const eq = pair.indexOf("=");
            return eq >= 0 ? pair.slice(0, eq) + "=***" : "***";
          })
          .join("&");
  return url.slice(0, schemeEnd) + authority + path + maskedQuery + fragment;
};

// Every URL inside a string, scrubbed; the rest of the string is kept.
export const scrubUrls = (text) => text.replace(URL_PATTERN, scrubUrl);

// Deep redaction of a parsed JSON value: a key that is sensitive by name
// (pattern or header map) gets "********" when set; strings get their URLs
// scrubbed; everything else is kept.
const redactDeep = (value) => {
  if (Array.isArray(value)) return value.map(redactDeep);
  if (isPlainObject(value)) {
    return Object.keys(value).reduce((acc, key) => {
      const v = value[key];
      acc[key] = isSensitiveName(key) ? (isEmpty(v) ? v : HISTORY_MASK) : redactDeep(v);
      return acc;
    }, {});
  }
  if (typeof value === "string") return scrubUrls(value);
  return value;
};

// NEW-A: the recorded form of a (non-secret-by-name) string. A string that
// parses as a JSON object/array is recorded as its REDACTED JSON (structure
// kept, secrets masked); any other string has its URLs scrubbed.
export const redactString = (text) => {
  const trimmed = text.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object") {
        return JSON.stringify(redactDeep(parsed));
      }
    } catch (e) {
      // not JSON: fall through to URL scrubbing
    }
  }
  return scrubUrls(text);
};

// A deep-comparable form of a value: identical deep copies compare equal, and
// null/undefined equal "". A value that can't be serialised never compares
// equal to anything (it is always treated as changed).
const comparable = (value) => {
  try {
    const json = JSON.stringify(isEmpty(value) ? "" : value);
    return json === undefined ? String(value) : json;
  } catch (e) {
    return Symbol("uncomparable");
  }
};

// The recorded text of one side of a change.
//   side: "old" | "new"; otherSide: the raw value on the other side.
export const toHistoryValue = (
  field,
  value,
  { side = "old", otherSide, clearFields = [] } = {}
) => {
  if (isSensitiveField(field) && !clearFields.includes(field)) {
    if (isPreMasked(value)) return value;
    if (isEmpty(value)) return "";
    return side === "new" && !isEmpty(otherSide) ? HISTORY_MASK_CHANGED : HISTORY_MASK;
  }
  if (value === null || value === undefined) return "";
  // NEW-A/NEW-C: strings - clearFields included, e.g. an AuthUrl - are still
  // scrubbed of URL credentials and, when JSON, deep-redacted.
  if (typeof value === "string") return redactString(value);
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  // A date is a scalar, not a structure (e.g. a customer's End date).
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  }
  return CHANGED_MARKER;
};

// The Changes list of a CrudHistory record. Inputs are never mutated.
export const getHistoryChanges = (oldData, newData, options = {}) => {
  const clearFields = options.clearFields || [];
  const oldObj = oldData && typeof oldData === "object" ? oldData : {};
  const newObj = newData && typeof newData === "object" ? newData : {};
  const changes = [];
  const record = (field) => {
    const oldValue = toHistoryValue(field, oldObj[field], {
      side: "old",
      otherSide: newObj[field],
      clearFields,
    });
    let newValue = toHistoryValue(field, newObj[field], {
      side: "new",
      otherSide: oldObj[field],
      clearFields,
    });
    // Only a redacted part changed (e.g. a token in a URL or a JSON string):
    // say so rather than recording two identical texts.
    if (newValue === oldValue && newValue !== "" && newValue !== CHANGED_MARKER) {
      newValue = newValue + " (changed)";
    }
    changes.push({ field, oldValue, newValue });
  };

  Object.keys(oldObj).forEach((field) => {
    if (EXCLUDED_FIELDS.includes(field)) return;
    if (comparable(oldObj[field]) === comparable(newObj[field])) return;
    record(field);
  });
  Object.keys(newObj).forEach((field) => {
    if (field in oldObj || field === "tableData") return;
    if (comparable(undefined) === comparable(newObj[field])) return;
    record(field);
  });
  return changes;
};
